import { AlertCircle, Loader2, type LucideIcon } from 'lucide-react';
import { useLocale } from '@/i18n/locale-provider';
import { NUAT_MARK_PATHS, NUAT_MARK_VIEWBOX, NUAT_WORDMARK_PATHS, NUAT_WORDMARK_VIEWBOX } from '@/brand/brand';

/* ------------------------------------------------------------------ brand */

export function NuatMark({ size = 32, tone = 'brand' }: { size?: number; tone?: 'brand' | 'inverse' }) {
  return (
    <svg width={size} height={size} viewBox={NUAT_MARK_VIEWBOX} role="img" aria-label="Nuat" focusable="false">
      <title>Nuat</title>
      {NUAT_MARK_PATHS.map((path, index) => (
        <path
          key={index}
          fill={
            tone === 'inverse'
              ? path.tone === 'deep' ? '#FFFFFF' : '#91D5E5'
              : path.tone === 'deep' ? '#355872' : '#91D5E5'
          }
          d={path.d}
        />
      ))}
    </svg>
  );
}

export function NuatWordmark({ height = 16, className = '' }: { height?: number; className?: string }) {
  const [, , width, viewHeight] = NUAT_WORDMARK_VIEWBOX.split(' ').map(Number);
  return (
    <svg
      height={height}
      width={(height * width) / viewHeight}
      viewBox={NUAT_WORDMARK_VIEWBOX}
      className={className}
      role="img"
      aria-label="NUAT"
      focusable="false"
    >
      <title>NUAT</title>
      <g fill="currentColor" transform={`translate(0 ${viewHeight}) scale(1 -1)`}>
        {NUAT_WORDMARK_PATHS.map((path, index) => (
          <path key={index} transform={`translate(${path.x} 0)`} d={path.d} />
        ))}
      </g>
    </svg>
  );
}

/* ------------------------------------------------------------------ state */

export function Spinner({ label }: { label?: string }) {
  const { t } = useLocale();
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-mist-500" role="status">
      <Loader2 size={18} className="animate-spin" aria-hidden />
      <span className="text-sm">{label ?? t.common.loading}…</span>
    </div>
  );
}

/**
 * A failure the user can act on.
 *
 * Shows the machine-readable code alongside the message: when someone reports a problem,
 * `INSUFFICIENT_STOCK` is far more useful to whoever fixes it than the prose alone.
 */
export function ErrorPanel({
  error, onRetry, title,
}: { error: unknown; onRetry?: () => void; title?: string }) {
  const { t } = useLocale();
  const code = (error as { code?: string })?.code;
  const message = (error as { message?: string })?.message ?? 'Unknown error';
  return (
    <div className="rounded-lg border border-danger-border bg-danger-bg p-5" role="alert">
      <div className="flex items-start gap-3">
        <AlertCircle size={19} className="mt-0.5 shrink-0 text-danger-fg" aria-hidden />
        <div className="min-w-0">
          <h3 className="font-semibold text-danger-fg">{title ?? t.common.errorTitle}</h3>
          <p className="mt-1 text-sm text-mist-700">{message}</p>
          {code && <p className="mt-1.5 font-mono text-xs text-mist-500">{code}</p>}
          {onRetry && (
            <button type="button" onClick={onRetry} className="btn-secondary mt-3 !py-1.5 !text-xs">
              {t.common.retry}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function EmptyState({
  icon: Icon, title, detail, action,
}: { icon: LucideIcon; title: string; detail?: string; action?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-mist-300 bg-white p-12 text-center">
      <Icon size={30} className="mx-auto text-mist-400" aria-hidden />
      <h3 className="mt-3 font-semibold text-mist-700">{title}</h3>
      {detail && <p className="mx-auto mt-1.5 max-w-md text-sm text-mist-500">{detail}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ layout */

export function PageHeader({
  title, subtitle, actions,
}: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="font-display text-2xl font-semibold text-deep-700">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-mist-600">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Card({
  title, action, children, className = '',
}: { title?: string; action?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={`rounded-lg border border-mist-200 bg-white ${className}`}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 border-b border-mist-200 px-5 py-3">
          {title && <h2 className="text-sm font-semibold text-deep-700">{title}</h2>}
          {action}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

/**
 * A KPI tile.
 *
 * `value` accepts null for "not available", which is rendered distinctly from zero —
 * a dashboard that shows 0 when it means "could not load" is actively misleading.
 */
export function Kpi({
  label, value, hint, tone = 'default',
}: {
  label: string;
  value: string | number | null;
  hint?: string;
  tone?: 'default' | 'good' | 'warn' | 'bad';
}) {
  const { t } = useLocale();
  const toneClass = {
    default: 'text-deep-600',
    good: 'text-success-fg',
    warn: 'text-warning-fg',
    bad: 'text-danger-fg',
  }[tone];

  return (
    <div className="rounded-lg border border-mist-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-mist-500">{label}</p>
      <p className={`tabular mt-1.5 font-display text-2xl font-semibold ${toneClass}`}>
        {value === null ? <span className="text-base text-mist-400">{t.common.unavailable}</span> : value}
      </p>
      {hint && <p className="mt-1 text-xs text-mist-500">{hint}</p>}
    </div>
  );
}

export function Badge({
  children, tone = 'neutral',
}: { children: React.ReactNode; tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'info' }) {
  const classes = {
    neutral: 'bg-mist-100 text-mist-700 border-mist-300',
    good: 'bg-success-bg text-success-fg border-success-border',
    warn: 'bg-warning-bg text-warning-fg border-warning-border',
    bad: 'bg-danger-bg text-danger-fg border-danger-border',
    info: 'bg-info-bg text-info-fg border-info-border',
  }[tone];
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${classes}`}>
      {children}
    </span>
  );
}

/** Map a domain status onto a badge tone, so colour is consistent across every screen. */
export function statusTone(status: string): 'neutral' | 'good' | 'warn' | 'bad' | 'info' {
  const good = ['CONFIRMED', 'FULFILLED', 'COMPLETED', 'PAID', 'ISSUED', 'HEALTHY', 'APPROVED', 'REFUNDED', 'MATCH', 'ok'];
  const warn = ['PENDING', 'AWAITING_PAYMENT', 'REQUESTED', 'RUNNING', 'UNTESTED', 'PARTIAL_MATCH', 'degraded', 'NEEDS_REVIEW'];
  const bad = ['CANCELLED', 'FAILED', 'PAYMENT_FAILED', 'REJECTED', 'SOURCE_UNAVAILABLE', 'down', 'stopped'];
  if (good.includes(status)) return 'good';
  if (warn.includes(status)) return 'warn';
  if (bad.includes(status)) return 'bad';
  return 'neutral';
}

export function Table({
  head, children, empty,
}: { head: string[]; children: React.ReactNode; empty?: boolean }) {
  const { t } = useLocale();
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[40rem] text-sm">
        <thead>
          <tr className="border-b border-mist-200 text-start">
            {head.map((column) => (
              <th key={column} className="whitespace-nowrap px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-mist-500">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-mist-100">
          {empty ? (
            <tr>
              <td colSpan={head.length} className="px-3 py-10 text-center text-mist-500">
                {t.common.nothingToShow}
              </td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
}
